import React, { useState, useEffect, useCallback } from 'react';

import * as Page from 'components/page';
import { RHP } from 'providers';

export function CorporateStep7({ useRankingCorporate, t }) {
    const {
        vars,
        getCompanyRankingApplication,
        postCompanyAuditEnrollment,
    } = useRankingCorporate;
    const { application, companyApplication } = vars;
    const { steps } = application;

    const enrollment = companyApplication?.auditEnrollment || { status: 'pending' };

    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Refetch when the tab regains focus (after the user comes back from
    // PagSeguro). The webhook should have already updated the status.
    const refresh = useCallback(() => {
        getCompanyRankingApplication().catch(() => {});
    }, [getCompanyRankingApplication]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
        };
    }, [refresh]);

    if (!steps || steps.step6 !== true) {
        RHP.replace('/ranking/latam/corporate/');
        return null;
    }

    const onClickEnroll = async () => {
        setSubmitting(true);
        setErrorMsg(null);
        try {
            const redirectUrl = window.location.href;
            const result = await postCompanyAuditEnrollment({ redirectUrl });
            if (result?.paymentLink && result.status === 'processing') {
                window.location.href = result.paymentLink;
            }
        } catch (err: any) {
            console.error(err);
            setErrorMsg(
                err?.response?.data?.message ||
                err?.message ||
                t('CorporateStep7.errorGeneric')
            );
        } finally {
            setSubmitting(false);
        }
    };

    const onClickReopenLink = () => {
        if (enrollment.paymentLink) {
            window.location.href = enrollment.paymentLink;
        }
    };

    const renderStatus = () => {
        if (enrollment.status === 'confirmed') {
            return (
                <div style={{ padding: '1rem', background: '#e6f7ed', borderRadius: 4 }}>
                    <strong>{t('CorporateStep7.statusConfirmedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0' }}>
                        {t('CorporateStep7.statusConfirmedDescr')}
                    </p>
                </div>
            );
        }
        if (enrollment.status === 'processing') {
            return (
                <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: 4 }}>
                    <strong>{t('CorporateStep7.statusProcessingTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {t('CorporateStep7.statusProcessingDescr')}
                    </p>
                    {enrollment.paymentLink && (
                        <button
                            type="button"
                            onClick={onClickReopenLink}
                            className="btn btn-primary"
                        >
                            {t('CorporateStep7.reopenPaymentLink')}
                        </button>
                    )}
                </div>
            );
        }
        if (enrollment.status === 'failed') {
            return (
                <div style={{ padding: '1rem', background: '#fdecea', borderRadius: 4 }}>
                    <strong>{t('CorporateStep7.statusFailedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {t('CorporateStep7.statusFailedDescr')}
                    </p>
                    <button
                        type="button"
                        onClick={onClickEnroll}
                        disabled={submitting}
                        className="btn btn-primary"
                    >
                        {t('CorporateStep7.tryAgain')}
                    </button>
                </div>
            );
        }
        return (
            <div style={{ padding: '1rem', background: '#f2f2f2', borderRadius: 4 }}>
                <strong>{t('CorporateStep7.statusPendingTitle')}</strong>
                <p style={{ margin: '0.5rem 0 0.75rem' }}>
                    {t('CorporateStep7.statusPendingDescr')}
                </p>
                {/* INICIO DO BOTAO PAGBANK */}
                <a
                    href="https://pag.ae/81MvGkVB7/button"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Pagar com PagBank"
                >
                    <img
                        src="https://assets.pagseguro.com.br/ps-integration-assets/botoes/pagamentos/205x30-pagar-azul.gif"
                        alt="Pague com PagBank - é rápido, grátis e seguro!"
                    />
                </a>
                {/* FIM DO BOTAO PAGBANK */}
            </div>
        );
    };

    return (
        <Page.Page
            name="CorporateStep7"
            back="/ranking/latam/corporate"
            customBackText={t('glossary:backToRanking')}
        >
            <Page.Title>{t('CorporateStep7.title')}</Page.Title>
            <Page.Descr>{t('CorporateStep7.description')}</Page.Descr>

            <p style={{ fontSize: '0.9em', color: '#666', marginTop: 12 }}>
                {t('CorporateStep7.priceNote')}
            </p>

            <h3 style={{ marginTop: 24 }}>{t('CorporateStep7.statusSectionTitle')}</h3>
            {renderStatus()}

            {errorMsg && (
                <p style={{ color: '#dc3545', marginTop: 12 }}>{errorMsg}</p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <Page.Buttn
                    style={{ width: '20%' }}
                    // @ts-ignore
                    to="/ranking/latam/corporate/step6"
                >
                    <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                    <span className="text">{t('glossary:back')}</span>
                </Page.Buttn>

                <Page.Buttn
                    style={{ width: '80%' }}
                    // @ts-ignore
                    to="/ranking/latam/corporate"
                >
                    <span className="text">{t('glossary:backToRanking')}</span>
                </Page.Buttn>
            </div>
        </Page.Page>
    );
}
